import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Input,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';
import withRouter from '../../../../components/Common/withRouter';
import TableContainer from '../../../../components/Common/TableContainer';
import { loadCNPSReport } from '../../../../slice/thunks';
import { useReactToPrint } from 'react-to-print';
import PrintHeader from '../../../../components/Global/PrintHeader';

const CNPSReports = (props) => {
  const dispatch = useDispatch();
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonthName, setCurrentMonthName] = useState("");

  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "CNPS Report",
  });

  const selectPayrollState = (state) => state.Payroll;
  const payrollData = createSelector(selectPayrollState, (payroll) => ({
    cnpsReport: payroll.cnpsReportByMonth || [],
    loading: payroll.loading,
  }));

  const { cnpsReport } = useSelector(payrollData);

  const months = [
    props.t("january"),
    props.t("february"),
    props.t("march"),
    props.t("april"),
    props.t("may"),
    props.t("june"),
    props.t("july"),
    props.t("august"),
    props.t("september"),
    props.t("october"),
    props.t("november"),
    props.t("december"),
  ];

  const years = Array.from(
    { length: 10 },
    (_, i) => new Date().getFullYear() - i
  );

  useEffect(() => {
    if (currentMonth && currentYear) {
      dispatch(loadCNPSReport({ year: currentYear, month: currentMonth }));
      setCurrentMonthName(months[currentMonth - 1]);
    }
  }, [currentMonth, currentYear, dispatch]);

  const columns = [
    {
      header: props.t("sn"),
      accessorKey: "sn",
      enableColumnFilter: false,
      cell: (cell) => cell.row.index + 1,
    },
    {
      header: props.t("fullName"),
      accessorKey: "fullName",
      enableColumnFilter: false,
    },
    {
      header: props.t("basic_salary"),
      accessorKey: "basicSalary",
      enableColumnFilter: false,
      cell: (cell) => cell.getValue()?.toLocaleString(),
    },
    {
      header: props.t("employer_share"),
      accessorKey: "employerShare",
      enableColumnFilter: false,
      cell: (cell) => cell.getValue()?.toLocaleString(),
    },
    {
      header: props.t("employee_share"),
      accessorKey: "employeeShare",
      enableColumnFilter: false,
      cell: (cell) => cell.getValue()?.toLocaleString(),
    },
    {
      header: props.t("total"),
      enableColumnFilter: false,
      cell: (cell) => {
        const total = (cell.row.original.employerShare || 0) + (cell.row.original.employeeShare || 0);
        return total.toLocaleString();
      },
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content p-3">
        <div className="container-fluid">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="fw-bold m-0">{props.t("cnps_report")}</h5>
            <div className="d-flex gap-2">
              <Button color="primary" onClick={handlePrint}>
                <i className="fa fa-print me-1"></i> {props.t("print")}
              </Button>
            </div>
          </div>

          <Card>
            <CardBody>
              <Row className="mb-3">
                <Col md={3}>
                  <Label>{props.t("select_year")}</Label>
                  <select
                    className="form-select"
                    value={currentYear}
                    onChange={(e) => setCurrentYear(e.target.value)}
                  >
                    <option value="">{props.t("select_year")}</option>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col md={3}>
                  <Label>{props.t("select_month")}</Label>
                  <select
                    className="form-select"
                    value={currentMonth}
                    onChange={(e) => setCurrentMonth(e.target.value)}
                  >
                    <option value="">{props.t("select_month")}</option>
                    {months.map((month, index) => (
                      <option key={month} value={index + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                </Col>
              </Row>

              <TableContainer
                columns={columns}
                data={cnpsReport || []}
                // isGlobalFilter={true}
                customPageSize={10}
                className="custom-header-repo"
                footer={
                  <tr className="fw-bold bg-light">
                    <td colSpan="2" className="text-end">{props.t("total").toUpperCase()}</td>
                    <td>
                      {cnpsReport?.reduce((acc, curr) => acc + (curr.basicSalary || 0), 0).toLocaleString()}
                    </td>
                    <td>
                      {cnpsReport?.reduce((acc, curr) => acc + (curr.employerShare || 0), 0).toLocaleString()}
                    </td>
                    <td>
                      {cnpsReport?.reduce((acc, curr) => acc + (curr.employeeShare || 0), 0).toLocaleString()}
                    </td>
                    <td>
                      {cnpsReport?.reduce((acc, curr) => acc + (curr.employerShare || 0) + (curr.employeeShare || 0), 0).toLocaleString()}
                    </td>
                  </tr>
                }
              />
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Hidden Print Section */}
      <div className="d-none">
        <div ref={printRef} className="p-4">
          <PrintHeader />
          <div className="text-center mb-4">
            <h4>{props.t("cnps_report").toUpperCase()}</h4>
            <h5>{currentMonthName} {currentYear}</h5>
          </div>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{props.t("sn")}</th>
                <th>{props.t("fullName")}</th>
                <th>{props.t("basic_salary")}</th>
                <th>{props.t("employer_share")}</th>
                <th>{props.t("employee_share")}</th>
                <th>{props.t("total")}</th>
              </tr>
            </thead>
            <tbody>
              {cnpsReport && cnpsReport.length > 0 ? (
                cnpsReport.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.fullName}</td>
                    <td>{item.basicSalary?.toLocaleString()}</td>
                    <td>{item.employerShare?.toLocaleString()}</td>
                    <td>{item.employeeShare?.toLocaleString()}</td>
                    <td>{((item.employerShare || 0) + (item.employeeShare || 0)).toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">{props.t("no_result_found")}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="fw-bold">
                <td colSpan="2" className="text-end">{props.t("total").toUpperCase()}</td>
                <td>
                  {cnpsReport?.reduce((acc, curr) => acc + (curr.basicSalary || 0), 0).toLocaleString()}
                </td>
                <td>
                  {cnpsReport?.reduce((acc, curr) => acc + (curr.employerShare || 0), 0).toLocaleString()}
                </td>
                <td>
                  {cnpsReport?.reduce((acc, curr) => acc + (curr.employeeShare || 0), 0).toLocaleString()}
                </td>
                <td>
                  {cnpsReport?.reduce((acc, curr) => acc + (curr.employerShare || 0) + (curr.employeeShare || 0), 0).toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </React.Fragment>
  );
};

export default withRouter(withTranslation()(CNPSReports));